import React, {useState, useEffect} from "react";
import styled from "styled-components";
import {Row, Card, Form, Col} from "react-bootstrap";
import ValidationError from '../../../services/ValidationError';
import SuccessButton from '../../organisms/SuccessButton';
import CardTitle from "../../organisms/CardTitle";
import ServerValidationErrorAlert from '../../organisms/ServerValidationErrorAlert';
import ServerErrorModal from '../../organisms/ServerErrorModal';
import ServerErrorAlert from '../../organisms/ServerErrorAlert';
import LoadingScreen from '../../organisms/LoadingScreen';
import {
    servicioGuarderiaAFormState,
    FormState,
    FormValue,
    initialFormState,
    invalidateStates,
    formStateAServicioGuarderia
} from './FormState';
import {capitalize} from '../../../utils/textUtils';
import {ValidationErrorResponse} from '../../../services/ValidationError';
import {useParams} from 'react-router-dom';
import {StyledFormControl} from "../../organisms/StyledFormControl";
import CurrencyFormat from "react-currency-format";
import {reemplazarServicioGuarderia, useBuscarServicioGuarderia} from "../../../services/guarderiaServicios";
import {useMutation} from "@tanstack/react-query";
import Exito, {ExitoOption} from "../../organisms/Exito";

function FormSection() {
    const {idServicio} = useParams();
    const {isLoading, data, isSuccess} = useBuscarServicioGuarderia(idServicio);
    const [validatedForm, setValidatedForm] = useState(false);
    const [formState, setFormState] = useState(initialFormState);
    const [serverFailedValidations, setServerFailedValidations] = useState({} as ValidationErrorResponse);
    const [serverErrorOcurred, setServerErrorOcurred] = useState(false);
    const [isFinished, setIsFinished] = useState(false);

    const reemplazo = useMutation(reemplazarServicioGuarderia, {
        onSuccess: (data, variables, context) => {
            setIsFinished(true);
        },
        onError: (error, variables, context) => {
            if (error instanceof ValidationError) {
                invalidateStates(formState, error.getFailedValidations());
                setServerFailedValidations(error.getFailedValidations());
            } else {
                setServerErrorOcurred(true);
            }
        }
    });

    const handleSubmit = (event: React.SyntheticEvent) => {
        event.preventDefault();

        const form = event.currentTarget as HTMLFormElement;

        if (!form.checkValidity()) {
            event.stopPropagation();
        } else {
            save();
        }

        setValidatedForm(true);
    };

    useEffect(() => {
        if (isSuccess && data) {
            setFormState(servicioGuarderiaAFormState(data));
        }
    }, [isSuccess, data]);

    const onInputChanged = ({target}: any) => {
        const newInputState = {
            ...formState[target.name as keyof FormState],
            value: target.value,
            isValid: target.checkValidity()
        };
        setFormState({...formState, [target.name]: newInputState});
    }

    const onInputChangedCapital = ({target}: any) => {
        const newInputState = {
            ...formState[target.name as keyof FormState],
            value: capitalize(target.value),
            isValid: target.checkValidity()
        };
        setFormState({...formState, [target.name]: newInputState});
    }

    const handleBlur = ({target}: any) => {
        const newInputState = {...formState[target.name as keyof FormState], wasTouched: true};
        setFormState({...formState, [target.name]: newInputState});
    }

    const save = async () => {
        setServerFailedValidations({} as ValidationErrorResponse);
        setServerErrorOcurred(false);
        if (idServicio) {
            reemplazo.mutate(formStateAServicioGuarderia(idServicio, formState));
        }
    }

    if(isFinished){
        return (<Exito title={'¡Modificación exitosa!'} options={exitoOptions}/>);
    }

    return (
        <Row className="justify-content-center">
            <div className="col-xs-12 col-md-10 col-lg-8 col-xxl-5">
                <LoadingScreen isLoading={isLoading || reemplazo.isLoading}/>
                <Card>
                    <StyledCardHeader className="pt-4 pb-4">
                        <CardTitle>Modifica un plan de guardería</CardTitle>
                    </StyledCardHeader>
                    <Card.Body>

                        {
                            serverFailedValidations && Object.keys(serverFailedValidations).length > 0 &&
                            <Row className="justify-content-center mb-3">
                                <ServerValidationErrorAlert serverFailedValidations={serverFailedValidations}
                                                            wrapperClass="col col-9"/>
                            </Row>
                        }

                        {
                            serverErrorOcurred &&
                            <Row className="justify-content-center mb-3">
                                <ServerErrorAlert class="col col-9"/>
                            </Row>
                        }

                        {
                            serverErrorOcurred &&
                            <ServerErrorModal/>
                        }

                        <Row className="justify-content-center">
                            <Form className="col col-9" noValidate validated={validatedForm}
                                  onSubmit={handleSubmit}>

                                <Form.Group as={Col} controlId="formNombre" className="mb-2">
                                    <Form.Label>Nombre</Form.Label>
                                    <StyledFormControl type="text" name="nombre"
                                                       value={formState.nombre.value} onChange={onInputChangedCapital}
                                                       required
                                                       onBlur={handleBlur}
                                                       isValid={isRequiredFormValueValid(formState.nombre)}
                                                       isInvalid={isRequiredFormValueInvalid(formState.nombre)}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        Campo debe ser diligenciado.
                                    </Form.Control.Feedback>
                                </Form.Group>

                                <Form.Group as={Col} controlId="formDescripcion" className="mb-2">
                                    <Form.Label>Descripción</Form.Label>
                                    <Form.Control as="textarea" name="descripcion"
                                                  value={formState.descripcion.value} onChange={onInputChanged}
                                                  onBlur={handleBlur} rows={3}
                                                  isValid={isFormValueValid(formState.descripcion)}
                                                  isInvalid={isFormValueInvalid(formState.descripcion)}
                                    />
                                    <Form.Text muted>
                                        Opcional.
                                    </Form.Text>
                                </Form.Group>

                                <Form.Group as={Col} controlId="formDias" className="mb-3">
                                    <Form.Label>Días en el mes</Form.Label>
                                    <StyledFormControl type="number" name="dias"
                                                       value={formState.dias.value} onChange={onInputChanged}
                                                       onBlur={handleBlur}
                                                       isValid={isRequiredFormValueValid(formState.dias)}
                                                       isInvalid={isRequiredFormValueInvalid(formState.dias)}
                                                       required
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        Campo debe ser un entero.
                                    </Form.Control.Feedback>
                                </Form.Group>

                                <Form.Group as={Col} controlId="formPrecioTotal" className="mb-3">
                                    <Form.Label>Precio Total</Form.Label>
                                    <CurrencyFormat customInput={Form.Control} name="precioTotal"
                                                    value={formState.precioTotal.value}
                                                    thousandSeparator={","}
                                                    onBlur={handleBlur}
                                                    isValid={isRequiredFormValueValid(formState.precioTotal)}
                                                    isInvalid={isRequiredFormValueInvalid(formState.precioTotal)}
                                                    required
                                                    onChange={onInputChanged}
                                                    prefix={'$'}
                                                    decimalSeparator={"."}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        Campo debe ser un número.
                                    </Form.Control.Feedback>
                                </Form.Group>

                                <Form.Group as={Col} controlId="formHoraSalida" className="mb-5">
                                    <Form.Label>Hora Salida</Form.Label>
                                    <StyledFormControl type="number" name="horaSalida"
                                                       value={formState.horaSalida.value} onChange={onInputChanged}
                                                       onBlur={handleBlur}
                                                       isValid={isRequiredFormValueValid(formState.horaSalida)}
                                                       isInvalid={isRequiredFormValueInvalid(formState.horaSalida)}
                                                       min="0" max="23"
                                                       required
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        Campo debe ser un número entre 0 y 23.
                                    </Form.Control.Feedback>
                                </Form.Group>

                                <ButtonsCol className="d-flex justify-content-center">
                                    <SuccessButton id="buttonFinalizar" type="submit" variant="success"
                                    >Guardar</SuccessButton>
                                </ButtonsCol>
                            </Form>
                        </Row>

                    </Card.Body>
                </Card>
            </div>
        </Row>
    );
}


const ButtonsCol = styled(Col)`
  gap: 1%;
`

const StyledCardHeader = styled(Card.Header)`
  border-color: #eaeaea;
  position: relative;
  background: transparent;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 0px;
`

const isFormValueValid = (formValue: FormValue): boolean => formValue.isValid === true && formValue.wasTouched
const isRequiredFormValueValid = (formValue: FormValue): boolean => formValue.isValid === true
const isRequiredFormValueInvalid = (formValue: FormValue): boolean => formValue.isValid === false
const isFormValueInvalid = (formValue: FormValue): boolean => formValue.isValid === false && formValue.wasTouched

const exitoOptions: ExitoOption[] = [
    {
        description: 'Crear nuevo servicio',
        path: '/servicios/guarderia/crear',
        variant: 'primary'
    },
    {
        description: 'Ver listado',
        path: '/servicios/guarderia',
        variant: 'secondary',
        isLast: true
    }
];

export default FormSection;